import { SpDropdownToggle } from 'components/shared/Dropdown';
import { FormattedMessage } from 'components/shared/Intl';
import { useToggle } from 'hooks/useToggle';
import { FC, useMemo } from 'react';
import { Dropdown, DropdownDivider, DropdownItem, DropdownMenu } from 'react-bootstrap';

import { connectedBatchFormOptionsMenu } from './FormOptionsMenu.connect';

export interface BatchFormOptionsMenuOwnProps {
  tabId: string;
  formId: string;
  isDisabled?: boolean;
  className?: string;
}

export interface BatchFormOptionsMenuStateProps {
  canDelete: boolean;
  canDuplicate: boolean;
  canPrice: boolean;
  canRefreshDates: boolean;
  canAbort: boolean;
  canOpenInQuoteWorkspace: boolean;
}

export interface BatchFormOptionsMenuDispatchProps {
  onDelete(): void;
  onDuplicate(): void;
  onPrice(): void;
  onResetDates(): void;
  onAbort(): void;
  onOpenInQuoteWorkspace(): void;
}

export type BatchFormOptionsMenuProps = BatchFormOptionsMenuOwnProps &
  BatchFormOptionsMenuStateProps &
  BatchFormOptionsMenuDispatchProps;

export const BatchFormOptionsMenuComponent: FC<BatchFormOptionsMenuProps> = ({
  canDelete,
  canDuplicate,
  canPrice,
  canRefreshDates,
  canAbort,
  canOpenInQuoteWorkspace,
  className,
  isDisabled = false,
  onDelete,
  onDuplicate,
  onPrice,
  onResetDates,
  onAbort,
  onOpenInQuoteWorkspace,
}) => {
  const { isOn, toggle } = useToggle();
  const shouldDisableMenu = useMemo(
    () => isDisabled || (!canDuplicate && !canDelete && !canPrice && !canRefreshDates && !canAbort),
    [isDisabled, canDuplicate, canDelete, canPrice, canRefreshDates, canAbort],
  );

  return (
    <Dropdown className={className} show={isOn} onToggle={toggle}>
      <SpDropdownToggle
        caret={false}
        className="p-0 m-0"
        variant="link"
        data-e2e="batch-option-menu-toggle"
        disabled={shouldDisableMenu}
      >
        <i className="icon icon-sm p-auto m-0">more_vert</i>
      </SpDropdownToggle>

      <DropdownMenu>
        <DropdownItem
          className="d-flex justify-content-between align-items-center"
          onClick={onDuplicate}
          disabled={isDisabled || !canDuplicate}
          data-e2e="batch-option-menu-duplicate"
        >
          <FormattedMessage id="duplicate" />
          <i className="icon icon-md">filter_none</i>
        </DropdownItem>

        <DropdownItem
          className="d-flex justify-content-between align-items-center gap-1"
          onClick={onOpenInQuoteWorkspace}
          disabled={isDisabled || !canOpenInQuoteWorkspace}
          data-e2e="batch-option-menu-open-in-quote-workspace"
        >
          <FormattedMessage id="openInQuoteWorkspace" />
          <i className="icon icon-md">open_in_new</i>
        </DropdownItem>

        <DropdownDivider />
        <DropdownItem
          className="d-flex justify-content-between align-items-center"
          onClick={onDelete}
          disabled={isDisabled || !canDelete}
          data-e2e="batch-option-menu-delete"
        >
          <FormattedMessage id="delete" />
          <i className="icon icon-md">delete_outline</i>
        </DropdownItem>
        <DropdownItem
          className="d-flex justify-content-between align-items-center"
          onClick={onResetDates}
          disabled={isDisabled || !canRefreshDates}
          data-e2e="batch-option-menu-refresh-dates"
        >
          <FormattedMessage id="form.ResetMainDates" />
          <i className="icon icon-md">settings_backup_restore</i>
        </DropdownItem>

        {canAbort && (
          <DropdownItem data-e2e="batch-option-menu-abort" disabled={isDisabled} onClick={onAbort}>
            <FormattedMessage id="abort" />
          </DropdownItem>
        )}
        <DropdownItem
          className="btn btn-success"
          data-e2e="batch-option-menu-price"
          disabled={isDisabled || !canPrice}
          onClick={onPrice}
        >
          <FormattedMessage id="price" />
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const BatchFormOptionsMenu = connectedBatchFormOptionsMenu(BatchFormOptionsMenuComponent);
